/** @jsxImportSource @emotion/react */
import { useState, useRef, useEffect } from 'react'
import { usePostHog } from 'posthog-js/react'
import { Product, SubscribeFormData, CheckoutRequest, CheckoutResponse } from '../../types'
import { Button } from '../UI/Button'

interface SubscribeModalProps {
  selectedProduct: Product | null
  isAffiliate: boolean
  affiliateReferrer?: string
  referralCode?: string
  token?: string
  onClose: () => void
  onLoading: (loading: boolean) => void
}

export const SubscribeModal = ({
  selectedProduct,
  isAffiliate,
  affiliateReferrer,
  referralCode,
  token,
  onClose,
  onLoading
}: SubscribeModalProps) => {
  const posthog = usePostHog()
  const [formData, setFormData] = useState<SubscribeFormData>({
    productId: selectedProduct?.id || '',
    fullName: '',
    email: '',
    phone: '',
    referralCodeInput: referralCode || '',
    agreeTerms: false
  })

  const formRef = useRef<HTMLFormElement>(null)

  // Prevent body scroll when modal is open
  useEffect(() => {
    document.body.style.overflow = 'hidden'
    
    // Cleanup function to reset body overflow when component unmounts
    return () => {
      document.body.style.overflow = 'unset'
    }
  }, [])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!selectedProduct) return

    const form = e.target as HTMLFormElement
    if (!form.checkValidity()) {
      form.reportValidity()
      return
    }

    onLoading(true)

    // Identify user in PostHog with their email
    posthog?.identify(formData.email, {
      email: formData.email,
      name: formData.fullName
    })

    const finalReferralCode = formData.referralCodeInput || referralCode

    const checkoutData: CheckoutRequest = {
      productId: selectedProduct.id,
      fullName: formData.fullName,
      email: formData.email,
      phone: formData.phone,
      successUrl: window.location.origin + '/success?session_id={CHECKOUT_SESSION_ID}',
      cancelUrl: window.location.origin,
      terms_accept_timestamp: new Date().toLocaleString('en-US', { 
        timeZone: 'America/Chicago'
      })
    }

    if (isAffiliate && affiliateReferrer) {
      checkoutData.affiliateTelegramUserId = affiliateReferrer
      if (token) {
        checkoutData.token = token
      }
    }

    if (finalReferralCode) {
      checkoutData.couponCode = finalReferralCode
    }

    try {
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/create-checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(checkoutData)
      })

      const data: CheckoutResponse = await response.json()

      if (data.status === 'success' && data.checkoutUrl) {
        window.location.href = data.checkoutUrl
      } else {
        alert(data.message || 'An error occurred. Please try again.')
        onLoading(false)
      }
    } catch (error) {
      console.error('Error creating checkout session:', error)
      alert('An error occurred. Please try again later.')
      onLoading(false)
    }
  }

  if (!selectedProduct) return null

  return (
    <>
      {/* Modal backdrop */}
      <div 
        className="modal-backdrop fade show" 
        style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1040 }}
        onClick={onClose}
      ></div>
      
      <div className="modal fade show" style={{ display: 'block', zIndex: 1050 }} tabIndex={-1}>
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Subscribe to {selectedProduct.name}</h5>
            <button 
              type="button" 
              className="btn-close btn-close-white" 
              onClick={onClose}
              aria-label="Close"
            ></button>
          </div>
          <div className="modal-body">
            <p className="mb-4">
              Please provide your information to continue with your subscription.
            </p>
            <form ref={formRef} onSubmit={handleSubmit}>
              <div className="form-floating mb-3">
                <input
                  type="text"
                  className="form-control"
                  id="fullName"
                  name="fullName"
                  placeholder="Full Name"
                  value={formData.fullName}
                  onChange={handleInputChange}
                  required
                />
                <label htmlFor="fullName">Full Name</label>
              </div>

              <div className="form-floating mb-3">
                <input
                  type="email"
                  className="form-control"
                  id="email"
                  name="email"
                  placeholder="Email address"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                />
                <label htmlFor="email">Email Address</label>
              </div>

              <div className="form-floating mb-3">
                <input
                  type="tel"
                  className="form-control"
                  id="phone"
                  name="phone"
                  placeholder="Phone Number"
                  value={formData.phone}
                  onChange={handleInputChange}
                />
                <label htmlFor="phone">Phone Number (optional)</label>
              </div>

              {!isAffiliate && (
                <div className="form-floating mb-3">
                  <input
                    type="text"
                    className="form-control"
                    id="referralCodeInput"
                    name="referralCodeInput"
                    placeholder="Referral Code"
                    value={formData.referralCodeInput}
                    onChange={handleInputChange}
                  />
                  <label htmlFor="referralCodeInput">Referral Code (optional)</label>
                </div>
              )}

              <div className="form-check mb-3">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="agreeTerms"
                  name="agreeTerms"
                  checked={formData.agreeTerms}
                  onChange={handleInputChange}
                  required
                />
                <label className="form-check-label" htmlFor="agreeTerms">
                  I agree to the{' '}
                  <a href="/terms-of-service" target="_blank" rel="noopener noreferrer">
                    Terms of service
                  </a>{' '}
                  and{' '}
                  <a href="/privacy-policy" target="_blank" rel="noopener noreferrer">
                    Privacy policy
                  </a>.
                </label>
              </div>
            </form>
          </div>
          <div className="modal-footer justify-content-end">
            <Button 
              variant="payment"
              onClick={() => {
                if (formRef.current) {
                  const submitEvent = new Event('submit', { bubbles: true, cancelable: true })
                  formRef.current.dispatchEvent(submitEvent)
                }
              }}
            >
              Proceed to Payment
            </Button>
          </div>
        </div>
      </div>
      </div>
    </>
  )
}